import { useContext, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Tabs from "@mui/material/Tabs";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import { LayoutContext } from "../../../context/LayoutContext";
import { getClientById } from "../../../services/api/client";
import ParentCustomerProfile from "./ParentCustomerProfile";
import { fetchUserDetails } from "../../../services/api/user";
import CustomerProfile from "../../ProfileDetails/CustomerProfile";
import CustomerAccountUsersTable from "../../NewUsers/CustomerAccountUsersTable";
import ChildClientListingTable from "../ChildClientListing/ChildClientListingTable";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import { brandFontFamily, brandInputTextColor, brandPrimaryColor } from "../../../utils/variables";

const CustomTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: "none",
  minWidth: 0,
  [theme.breakpoints.up("sm")]: {
    minWidth: 0,
  },
  height: "3px",
  width: "230px",
  fontWeight: 300,
  fontSize: "16px",
  boxShadow: "0px -1px 8px 0px rgba(0, 0, 0, 0.09)",
  borderRadius: "6px",
  margin: "13px 0",
  background: "white",
  marginRight: theme.spacing(1),
  color: brandInputTextColor,
  fontFamily: brandFontFamily,
  "&.Mui-selected": {
    color: brandPrimaryColor,
    fontWeight: 400,
    borderBottom: `1px solid ${brandPrimaryColor}`,
  },
}));

const ALL_TABS = [
  { key: "profile", label: "Profile" },
  { key: "details", label: "Company Details" },
  { key: "entities", label: "Entities" },
  { key: "users", label: "Users" },
];

const ParentCustomerDetails = () => {
  const { parentClientId } = useParams();
  const { isBannerVisible } = useContext(LayoutContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [actionsSlot, setActionsSlot] = useState(null);

  const { data: clientData } = useQuery({
    queryKey: ["client", parentClientId],
    queryFn: () => getClientById(parentClientId),
    enabled: !!parentClientId,
  });
  const parentClient = clientData?.data;
  const ownerId = parentClient?.ownerId;

  const tabs = ALL_TABS.filter((tab) => tab.key !== "profile" || ownerId);

  const requestedIndex = tabs.findIndex((tab) => tab.key === searchParams.get("tab"));
  const activeIndex =
    requestedIndex === -1 ? tabs.findIndex((tab) => tab.key === "details") : requestedIndex;
  const activeTab = tabs[activeIndex]?.key;

  const { data: owner } = useQuery({
    queryKey: ["userData", String(ownerId), false],
    queryFn: () => fetchUserDetails(ownerId),
    enabled: !!ownerId,
  });

  const handleChange = (event, newValue) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", tabs[newValue].key);
    setSearchParams(params, { replace: true });
  };

  return (
    <div
      className={`bg-[#F6F6F6] sm:fixed sticky 2xl:w-[95.2%] xl:w-[93.2%] lg:w-[90.6%] md:w-[87.5%] sm:w-[95%] w-full overflow-y-auto transition-all ${
        isBannerVisible ? "top-[106px] h-[calc(100vh-106px)]" : "top-[56px] h-[calc(100vh-56px)]"
      }`}
    >
      <div className="bg-white border-b border-zinc-300">
        <BaseWrapper>
          <div className="grid grid-cols-7 gap-y-3 py-[7px] items-center">
            <div className="md:col-span-3 col-span-7">
              <h3 className="font-light md:text-left text-center">
                {parentClient?.clientName || "Customer"}
              </h3>
            </div>
            <div
              ref={setActionsSlot}
              className="md:col-span-4 col-span-7 flex items-center justify-end gap-3"
            />
          </div>
        </BaseWrapper>
      </div>

      <BaseWrapper>
        <Tabs
          TabIndicatorProps={{ style: { display: "none" } }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          value={activeIndex}
          onChange={handleChange}
          aria-label="parent customer tabs"
          sx={{
            "& .MuiTabs-flexContainer": { overflow: "visible !important" },
            "& .MuiTabs-scrollButtons.Mui-disabled": { width: 0, opacity: 0 },
          }}
        >
          {tabs.map((tab) => (
            <CustomTab
              key={tab.key}
              label={tab.label}
              sx={{ minHeight: "38px", marginRight: "40px" }}
            />
          ))}
        </Tabs>
      </BaseWrapper>

      {activeTab === "profile" && (
        <BaseWrapper className="pb-6">
          {owner && <CustomerProfile user={owner} hideCustomerInfo />}
        </BaseWrapper>
      )}

      {activeTab === "details" && (
        <ParentCustomerProfile clientId={parentClientId} actionsContainer={actionsSlot} />
      )}

      {activeTab === "users" && <CustomerAccountUsersTable hideTitle customerId={parentClientId} />}

      {activeTab === "entities" && (
        <ChildClientListingTable hideTitle actionsContainer={actionsSlot} />
      )}
    </div>
  );
};

export default ParentCustomerDetails;
